import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export interface PaginationProps {
  /** Página atual, começando em 1. */
  page: number;
  /** Total de páginas. Ausente quando a lista pagina no servidor sem total conhecido. */
  pageCount?: number;
  /** Posição do primeiro e do último item da página, começando em 1. */
  rangeStart: number;
  rangeEnd: number;
  /** Total de itens, quando conhecido. */
  total?: number;
  hasPrevious: boolean;
  hasNext: boolean;
  onPrevious: () => void;
  onNext: () => void;
  /** Mostra o indicador de carregamento enquanto a próxima página chega. */
  loading?: boolean;
  /** Nome do item no plural, para o resumo ("pedidos", "vendedores"). */
  itemLabel?: string;
  className?: string;
}

/**
 * Rodapé de paginação das listagens.
 *
 * Serve às duas formas de paginar do painel: a que corta em memória uma lista já
 * carregada (total conhecido) e a que pede a próxima página ao servidor por
 * cursor (total conhecido só quando há contagem por agregação). Por isso o
 * total e o número de páginas são opcionais, e o avanço depende só de
 * `hasNext`.
 */
export function Pagination({
  page,
  pageCount,
  rangeStart,
  rangeEnd,
  total,
  hasPrevious,
  hasNext,
  onPrevious,
  onNext,
  loading,
  itemLabel = "itens",
  className,
}: PaginationProps) {
  // Uma página só, sem nada antes nem depois: o rodapé seria ruído.
  if (!hasPrevious && !hasNext) return null;

  const summary =
    total != null
      ? `${rangeStart} a ${rangeEnd} de ${total.toLocaleString("pt-BR")} ${itemLabel}`
      : `${rangeStart} a ${rangeEnd} ${itemLabel}`;

  return (
    <nav
      aria-label="Paginação"
      className={cn(
        "flex flex-wrap items-center justify-between gap-2 border-t border-border px-3 py-2 sm:px-4",
        className,
      )}
    >
      <p className="min-w-0 text-caption text-muted-foreground tabular-nums" aria-live="polite">
        {summary}
      </p>
      <div className="flex items-center gap-1">
        {loading ? <Loader2 className="size-4 animate-spin text-muted-foreground" aria-hidden /> : null}
        <Button
          variant="ghost"
          size="sm"
          onClick={onPrevious}
          disabled={!hasPrevious || loading}
          aria-label="Página anterior"
        >
          <ChevronLeft />
          <span className="hidden xs:inline">Anterior</span>
        </Button>
        <span className="px-1 text-caption font-semibold tabular-nums">
          {pageCount ? `${page} de ${pageCount}` : `Página ${page}`}
        </span>
        <Button
          variant="ghost"
          size="sm"
          onClick={onNext}
          disabled={!hasNext || loading}
          aria-label="Próxima página"
        >
          <span className="hidden xs:inline">Próxima</span>
          <ChevronRight />
        </Button>
      </div>
    </nav>
  );
}
